import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrograms } from "@/context/ProgramContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Save, FolderPlus, Tv, Users as UsersIcon,
  Edit3, X,
} from "lucide-react";

import { BRAND_MONOGRAM, DEFAULT_PROGRAM_LOGO } from "@/lib/brand";

const DEFAULT_LOGO = DEFAULT_PROGRAM_LOGO;

export default function ProgramsAdminPage() {
  const { user } = useAuth();
  const { refresh: refreshPrograms } = usePrograms() || {};
  const nav = useNavigate();
  const [groups, setGroups] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // new group / program forms
  const [newGroupName, setNewGroupName] = useState("");
  const [newProgram, setNewProgram] = useState({ group_id: "", name: "", logo_url: "" });
  const [editingProgram, setEditingProgram] = useState(null); // { id, name, logo_url, group_id }
  const [assignOpen, setAssignOpen] = useState(null); // program_id

  const load = async () => {
    setLoading(true);
    try {
      const [g, p, u] = await Promise.all([
        api.get("/admin/groups"),
        api.get("/admin/programs"),
        api.get("/admin/users"),
      ]);
      setGroups(g.data);
      setPrograms(p.data);
      setUsers(u.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Apenas o responsável pode acessar.");
        nav("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const createGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    await api.post("/admin/groups", { name });
    setNewGroupName("");
    toast.success("Grupo criado.");
    load();
  };

  const removeGroup = async (id) => {
    if (!window.confirm("Excluir este grupo? Só é possível se não houver programas vinculados.")) return;
    try {
      await api.delete(`/admin/groups/${id}`);
      toast.success("Grupo removido.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir.");
    }
  };

  const createProgram = async () => {
    if (!newProgram.group_id || !newProgram.name.trim()) {
      toast.message("Escolha um grupo e informe o nome do programa.");
      return;
    }
    try {
      await api.post("/admin/programs", {
        group_id: newProgram.group_id,
        name: newProgram.name.trim(),
        logo_url: newProgram.logo_url.trim(),
      });
      setNewProgram({ group_id: "", name: "", logo_url: "" });
      toast.success("Programa criado.");
      await load();
      if (refreshPrograms) await refreshPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar programa.");
    }
  };

  const saveEditing = async () => {
    if (!editingProgram) return;
    try {
      await api.patch(`/admin/programs/${editingProgram.id}`, {
        name: editingProgram.name.trim(),
        logo_url: editingProgram.logo_url.trim(),
        group_id: editingProgram.group_id,
      });
      setEditingProgram(null);
      toast.success("Programa atualizado.");
      await load();
      if (refreshPrograms) await refreshPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar.");
    }
  };

  const removeProgram = async (id) => {
    if (!window.confirm("Excluir este programa? Só é possível se não houver conteúdo (episódios, pautas, gaveta).")) return;
    try {
      await api.delete(`/admin/programs/${id}`);
      toast.success("Programa removido.");
      await load();
      if (refreshPrograms) await refreshPrograms();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao excluir.");
    }
  };

  const toggleUserProgram = async (userId, programId, hasAccess) => {
    const u = users.find((x) => x.id === userId);
    if (!u) return;
    const next = hasAccess
      ? u.program_ids.filter((p) => p !== programId)
      : [...u.program_ids, programId];
    try {
      await api.post(`/admin/users/${userId}/programs`, { program_ids: next });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao atualizar.");
    }
  };

  const programsByGroup = (gid) => programs.filter((p) => p.group_id === gid);

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost" size="sm" data-testid="back-to-calendar"
              onClick={() => nav("/")} className="rounded-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Calendário
            </Button>
            <img src={BRAND_MONOGRAM} alt="PodiUmScript" className="h-9 w-auto" />
            <div className="border-l border-zinc-200 pl-4">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500 flex items-center gap-1.5">
                <Tv className="w-3 h-3" /> Admin
              </div>
              <div className="font-heading text-lg font-bold tracking-tight">Grupos e Programas</div>
            </div>
          </div>
          <div className="text-sm text-zinc-600 hidden md:inline">{user?.name || user?.email}</div>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 py-8 space-y-10">
        {/* Groups section */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-[0.3em] font-bold text-zinc-500 flex items-center gap-2">
              <FolderPlus className="w-3 h-3" /> Grupos
            </div>
            <span className="text-xs text-zinc-500">{groups.length}</span>
          </div>
          <Card className="rounded-sm border-zinc-200 shadow-none p-4 flex items-center gap-2">
            <Input
              data-testid="new-group-name"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Nome do novo grupo (ex: TV Local)"
              className="rounded-sm border-zinc-300 flex-1"
              onKeyDown={(e) => { if (e.key === "Enter") createGroup(); }}
            />
            <Button
              data-testid="create-group-btn"
              onClick={createGroup}
              className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white h-10"
            >
              <Plus className="w-4 h-4 mr-1" /> Criar grupo
            </Button>
          </Card>
        </section>

        {/* Groups + Programs */}
        {loading ? (
          <div className="text-sm text-zinc-500">Carregando...</div>
        ) : groups.length === 0 ? (
          <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-8 text-center text-sm text-zinc-500">
            Nenhum grupo cadastrado ainda.
          </Card>
        ) : (
          groups.map((g) => (
            <section key={g.id} data-testid={`group-section-${g.id}`} className="space-y-4">
              <div className="flex items-center justify-between border-b border-zinc-200 pb-2">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-zinc-500">Grupo</div>
                  <div className="font-heading text-2xl font-bold tracking-tight">{g.name}</div>
                </div>
                <button
                  data-testid={`delete-group-${g.id}`}
                  onClick={() => removeGroup(g.id)}
                  className="text-xs text-zinc-400 hover:text-red-600 flex items-center gap-1"
                  title="Excluir grupo"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Excluir grupo
                </button>
              </div>

              {/* New program under this group */}
              <Card className="rounded-sm border-zinc-200 shadow-none p-4">
                <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-3">
                  Novo programa neste grupo
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input
                    data-testid={`new-program-name-${g.id}`}
                    placeholder="Nome do programa"
                    value={newProgram.group_id === g.id ? newProgram.name : ""}
                    onChange={(e) => setNewProgram({ group_id: g.id, name: e.target.value, logo_url: newProgram.group_id === g.id ? newProgram.logo_url : "" })}
                    className="rounded-sm border-zinc-300"
                  />
                  <Input
                    data-testid={`new-program-logo-${g.id}`}
                    placeholder="URL da logo (opcional)"
                    value={newProgram.group_id === g.id ? newProgram.logo_url : ""}
                    onChange={(e) => setNewProgram({ group_id: g.id, name: newProgram.group_id === g.id ? newProgram.name : "", logo_url: e.target.value })}
                    className="rounded-sm border-zinc-300"
                  />
                  <Button
                    data-testid={`create-program-${g.id}`}
                    onClick={() => { setNewProgram((p) => ({ ...p, group_id: g.id })); createProgram(); }}
                    className="rounded-sm bg-[#152D4B] hover:bg-[#0E1F36] text-white"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Criar programa
                  </Button>
                </div>
              </Card>

              {/* Programs grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {programsByGroup(g.id).map((p) => (
                  <Card
                    key={p.id}
                    data-testid={`program-card-${p.id}`}
                    className="rounded-sm border-zinc-200 shadow-none p-4"
                  >
                    {editingProgram?.id === p.id ? (
                      <div className="space-y-3">
                        <Input
                          data-testid={`edit-program-name-${p.id}`}
                          value={editingProgram.name}
                          onChange={(e) => setEditingProgram({ ...editingProgram, name: e.target.value })}
                          className="rounded-sm border-zinc-300 font-bold"
                        />
                        <Input
                          data-testid={`edit-program-logo-${p.id}`}
                          placeholder="URL da logo"
                          value={editingProgram.logo_url}
                          onChange={(e) => setEditingProgram({ ...editingProgram, logo_url: e.target.value })}
                          className="rounded-sm border-zinc-300 text-xs"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            data-testid={`save-program-${p.id}`}
                            onClick={saveEditing}
                            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white"
                          >
                            <Save className="w-3.5 h-3.5 mr-1" /> Salvar
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => setEditingProgram(null)}
                            className="rounded-sm"
                          >
                            <X className="w-3.5 h-3.5 mr-1" /> Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start gap-3">
                          <img
                            src={p.logo_url || DEFAULT_LOGO}
                            alt={p.name}
                            className="w-14 h-14 object-contain bg-zinc-50 border border-zinc-100 rounded-sm"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-heading text-lg font-bold tracking-tight truncate">{p.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">
                              {users.filter((u) => u.program_ids?.includes(p.id)).length} usuário(s)
                            </div>
                          </div>
                        </div>
                        <div className="mt-4 flex items-center gap-3 pt-3 border-t border-zinc-100">
                          <button
                            data-testid={`edit-program-${p.id}`}
                            onClick={() => setEditingProgram({ id: p.id, name: p.name, logo_url: p.logo_url || "", group_id: p.group_id })}
                            className="text-xs text-zinc-600 hover:text-[#152D4B] flex items-center gap-1"
                          >
                            <Edit3 className="w-3 h-3" /> Editar
                          </button>
                          <button
                            data-testid={`assign-users-${p.id}`}
                            onClick={() => setAssignOpen(assignOpen === p.id ? null : p.id)}
                            className="text-xs text-zinc-600 hover:text-[#152D4B] flex items-center gap-1"
                          >
                            <UsersIcon className="w-3 h-3" /> Atribuir usuários
                          </button>
                          <button
                            data-testid={`delete-program-${p.id}`}
                            onClick={() => removeProgram(p.id)}
                            className="ml-auto text-xs text-zinc-400 hover:text-red-600 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Excluir
                          </button>
                        </div>

                        {assignOpen === p.id && (
                          <div
                            data-testid={`user-assignment-${p.id}`}
                            className="mt-3 pt-3 border-t border-zinc-100 space-y-1"
                          >
                            {users
                              .filter((u) => u.approved && u.role !== "admin")
                              .map((u) => {
                                const has = u.program_ids?.includes(p.id);
                                return (
                                  <label
                                    key={u.id}
                                    className="flex items-center gap-2 py-1 px-1 hover:bg-zinc-50 cursor-pointer rounded-sm"
                                  >
                                    <Checkbox
                                      checked={has}
                                      onCheckedChange={() => toggleUserProgram(u.id, p.id, has)}
                                      data-testid={`assign-${p.id}-user-${u.id}`}
                                      className="rounded-sm"
                                    />
                                    <span className="text-sm text-zinc-700 flex-1 truncate">
                                      {u.name || u.email}
                                    </span>
                                    <span className="text-[9px] uppercase tracking-wider text-zinc-400">
                                      {u.email}
                                    </span>
                                  </label>
                                );
                              })}
                            {users.filter((u) => u.approved && u.role !== "admin").length === 0 && (
                              <div className="text-xs text-zinc-500 italic p-2 text-center">
                                Nenhum usuário aprovado ainda.
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </Card>
                ))}
                {programsByGroup(g.id).length === 0 && (
                  <Card className="rounded-sm border-dashed border-zinc-300 shadow-none p-6 text-center text-xs text-zinc-500 md:col-span-2">
                    Nenhum programa neste grupo ainda.
                  </Card>
                )}
              </div>
            </section>
          ))
        )}
      </main>
    </div>
  );
}
